import _ from 'lodash';
import {contains} from 'common/utils';
import getPennyPrecisionFunc, {
  pennyPrecisionFuncMap,
} from 'common/requestDetails/pennyPrecisionFunctions';

/**
 * 2026-01-06
 * History:
 * Legacy code that configured a BudgetSummaryListenerV2 (BSLV2) component for pages in
 * PrepFire FOs (for programs AFG, FPS, SAFER) made use of a recipientCostShareRules
 * config property for the AFG and FPS pages.  The BSLV2 called a
 * BudgetUtils.completeBudgetFromDataRules(params) function to generate a budget object
 * from the data provided in its params argument which included those rules.  We are
 * repurposing some nested functions defined within in that
 * BudgetUtils.completeBudgetFromDataRules(params) to process rules that are now
 * defined as a budgetCalcRules property within a budgetCalcConfig property of
 * fundingOpportunityConfig.json file.
 *
 * Some properties of rules were renamed (e.g., percentValue renamed defaultAppRate),
 * and logic tweaks have been made.
 * The FPS configuration didn't depend on application data, so what was a rule is now
 * part of a static configuration w/o rules.
 *
 * GMMGO-61724 - outcome "Prep:Fire - refactor budget calculations for consistence across grants life cycle"
 * GMMGO-67661 - epic "Prep:Fire refactor of budget calculations"
 *
 * Confluence page "Cost share and budget calculations refactor"
 * https://maestro.dhs.gov/confluence/spaces/GMMGO/pages/1586464150/Cost+share+and+budget+calculations+refactor
 * Child page "Budget Refactoring effort dev notes" ... hierarchy of pages documenting effort
 * https://maestro.dhs.gov/confluence/spaces/GMMGO/pages/1746408016/Budget+Refactoring+effort+dev+notes
 *
 * The class used to help interpret any rules that might exist in a budgetCalcConfig
 * property of the fundingOpportunityConfig.json file in order to come up with
 * a normalized budgetCalcConfig structure that has appRates property for each
 * of the fundingStreamType (fsType) values.  This is used by the AFG program funding
 * opportunities (FOs) to determine what appRate to use (defaultAppRate).  That is a
 * single value which has been configured to result in either 0.05, 0.10, 0.15
 * depending on what type of org you are and the population of your jurisdiction
 * (state or municipality). That single value is used to create an appRates array
 * with that single value appearing in the array for as many elements as there are periods,
 * (which has always be 1 so far for AFG).
 *
 * budgetCalcConfig.budgeteCalcRules is an array of "rule" objects.  A rule object can
 * have an ifDataPath property which is the path to a value in the appData passed in as
 * the context for which to interpret a rule.  Along with ifDataPath property is a
 * equalValues array property containing an array of values which which the value at the
 * ifDataPath path in the application is compared to.  If the ifDataPath value equals
 * one of the values in equalValues then that rule object is applicable for given appData.
 * The rule object also contains a calculationRules array.  Those objects can have an
 * ifDataPath as well.  If the "calcRule" object does not have an ifDataPath then that
 * calcRule is always considered applicable.  If an ifDataPath is present in the calcRule
 * object then it can be evaluated against an equalValues as is done with a rule object.
 * If equalValues property doesn't exist but a "range" property exists with two numbers,
 * [lowVal, hiVal], then the ifDataPath is considered a number and it is compared to the two
 * values to see if it falls within the range of the two numbers (val>=lowVal && val<=hiVal).
 * If it does then the calcRule object is considered applicable.
 *
 * The resulting applicable rules and calcRules get reduced to a single object containing
 * properties whose values come from property values of those applicable objects.  The
 * resulting object can have a "defaltAppRate", a "fedShareLimit", "fedShareLimitMessage",
 * "isEligible", and "notEligibleMessage" properties.  When that resulting object is
 * applied to the configuration used to instantiate this class, each object in the
 * budgetCalcConfig.fundingStreamConfig object will then have an appRates array containing
 * the value of defaultAppRate appearing numPeriod times.
 * budgetCalcConfig = {
 *   ...,
 *   fundingStreamConfigs: {
 *     "fsType1": {numPeriods: 1},
 *     "fsType2": {numPeriods: 2},
 *     "fsType3": {numPeriods: 3}
 *   },
 *   ...
 * }
 * becomes the following if evaluateRules(appData) result has defaultAppRate=0.15
 * budgetCalcConfig = {
 *   ...,
 *   fundingStreamConfigs: {
 *     "fsType1": {numPeriods: 1, appRates:[0.15]},
 *     "fsType2": {numPeriods: 2, appRates:[0.15, 0.15]},
 *     "fsType3": {numPeriods: 3, appRates:[0.15, 0.15, 0.15]}
 *   },
 *   ...
 * }
 */
class BudgetCalcRulesEvaluator {
  constructor(budgetCalcConfig) {
    if (_.isNil(budgetCalcConfig)) {
      throw new Error(
        'BudgetCalcRulesEvaluated requires a budgetCalcConfig in order to instantiate.'
      );
    }
    this.budgetCalcConfig = budgetCalcConfig;
    // clone a copy so we can update cnofig by applying evaluated rules if necessary w/o affecting original
    this.normalizedBudgetCalcConfig = JSON.parse(JSON.stringify(budgetCalcConfig));
    this.pennyPrecisionFunc = getPennyPrecisionFunc(
      budgetCalcConfig.pennyPrecisionFuncKey
    );
  }

  getNormalizedBudgetCalcConfig() {
    return this.normalizedBudgetCalcConfig;
  }

  getPennyPrecisionFunc() {
    return this.pennyPrecisionFunc;
  }

  hasRules() {
    return this.budgetCalcConfig?.budgetCalcRules ? true : false;
  }

  /*
   * Evaluate the rules for this configuration in context of the appData provided,
   * and return object representing configuration properties that can then be
   * applied to the this.budgetCalcConfig in order to get this.normalizedBudgetCalcConfig.
   */
  evaluateRules(appData) {
    // if no rules to evaluate, the simply return empty object
    if (_.isEmpty(this?.budgetCalcConfig?.budgetCalcRules)) {
      return {};
    }
    const applicableRules = this.getApplicableRules(appData);
    const applicableCalcRules = this.getApplicableCalcRules(applicableRules, appData);
    const reducedRules = this.reduceRulesArrayToObject(applicableRules); // may define a default defaultAppRate value
    const reducedCalcRules = this.reduceCalcRulesArrayToObject(applicableCalcRules);
    // defaultAppRate from calcRule takes precedence over one defined in rule.
    const dfltAppRate = _.get(
      reducedCalcRules,
      'defaultAppRate',
      reducedRules.defaultAppRate
    );
    // if defaultAppRate was not defined by applicable calcRules, but it is defined in rules,
    // use value from rules
    if (
      reducedCalcRules.defaultAppRate === undefined &&
      typeof dfltAppRate === 'number'
    ) {
      reducedCalcRules.defaultAppRate = dfltAppRate;
    }
    return reducedCalcRules;
  }

  applyEvaluatedRules(evaluatedRules) {
    this.normalizedBudgetCalcConfig = JSON.parse(JSON.stringify(this.budgetCalcConfig)); // reset clone of config
    if (_.isNumber(evaluatedRules?.defaultAppRate)) {
      const dfltRate = evaluatedRules.defaultAppRate;
      const fsCfgs = this.normalizedBudgetCalcConfig.fundingStreamConfigs;
      for (const fsType in fsCfgs) {
        const fsCfg = fsCfgs[fsType];
        if (!this.isValidAppRatesArray(fsCfg.appRates)) {
          fsCfg.appRates = _.fill(new Array(fsCfg.numPeriods), dfltRate);
        }
      }
    }
    if (typeof evaluatedRules.fedShareLimit === 'number') {
      this.normalizedBudgetCalcConfig.fedShareLimit = evaluatedRules.fedShareLimit;
    }
    if (evaluatedRules.fedShareLimitMessage) {
      this.normalizedBudgetCalcConfig.fedShareLimitMessage =
        evaluatedRules.fedShareLimitMessage;
    }
    if (typeof evaluatedRules.isEligible === 'boolean') {
      this.normalizedBudgetCalcConfig.isEligible = evaluatedRules.isEligible;
    }
    if (evaluatedRules.notEligibleMessage) {
      this.normalizedBudgetCalcConfig.notEligibleMessage =
        evaluatedRules.notEligibleMessage;
    }

    return this.normalizedBudgetCalcConfig;
  }

  /*  You can have rules, evaluate them, and apply them to the budgetCalcConfig,
   * but still end up with a budgetCalcConfig that is not normalized (able to be used
   * to calculate appShare/fedShare).
   *
   * When first starting an application you haven't entered your address with the state,
   * nor the permanent population size, nor your type of organization.  Without
   * the right combination of a couple of those things the evaluated rules will
   * not result in having a defaultAppRate property that can then be used to build
   * the appRates arrays for each of the fundingOpportunityType config objects.
   *
   * Return true if required properties exist with the legitimate values, false otherwise.
   * Configuration needs to have a pennyPrecisionFuncKey, and appRateBasedOn, and
   * each object in fundingStreamConfigs needs a numPeriods and appRates array.
   */
  isBudgetCalcConfigNormalized() {
    let reasonsItIsNotNormalized = [];
    const validPennyPrecisionFuncKeys = _.keys(pennyPrecisionFuncMap);
    const validBasedOnValues = ['federal', 'totalProjectCosts'];
    // verify pennyPrecisionFuncKey exists and has valid value
    if (
      !validPennyPrecisionFuncKeys.includes(
        this.normalizedBudgetCalcConfig.pennyPrecisionFuncKey
      )
    ) {
      reasonsItIsNotNormalized = _.concat(
        reasonsItIsNotNormalized,
        `Invalid pennyPrecisionFuncKey value: ${this.normalizedBudgetCalcConfig.pennyPrecisionFuncKey}`
      );
    }
    // verify appRateBasedOn exists and has valid value
    let basedOn = this.normalizedBudgetCalcConfig.appRateBasedOn;
    if (!_.includes(validBasedOnValues, basedOn)) {
      reasonsItIsNotNormalized = _.concat(
        reasonsItIsNotNormalized,
        `appRateBasedOn is invalid value: ${basedOn}`
      );
    }
    /* verify all fundingStreamConfigs properties have a numeric numPeriods
     * and and a non-empty appRates array with numeric values between 0 and 1,
     * and has numPeriods elements in the array
     */
    const fsConfigs = this.normalizedBudgetCalcConfig.fundingStreamConfigs;
    if (_.isEmpty(fsConfigs)) {
      reasonsItIsNotNormalized = _.concat(
        reasonsItIsNotNormalized,
        `fundingStreamConfigs is either undefined, or empty.`
      );
    } else {
      for (const fsType in this.normalizedBudgetCalcConfig.fundingStreamConfigs) {
        let fsConfig = this.normalizedBudgetCalcConfig.fundingStreamConfigs[fsType];
        if (typeof fsConfig.numPeriods != 'number') {
          reasonsItIsNotNormalized = _.concat(
            reasonsItIsNotNormalized,
            `Funding stream config ${fsType} "numPeriods" property is either missing or non-numeric.`
          );
        }
        if (fsConfig?.appRates === undefined) {
          reasonsItIsNotNormalized = _.concat(
            reasonsItIsNotNormalized,
            `Funding stream config ${fsType} is missing appRates array.`
          );
        } else if (!_.isArray(fsConfig.appRates)) {
          reasonsItIsNotNormalized = _.concat(
            reasonsItIsNotNormalized,
            `Funding stream config ${fsType} appRates is not array.`
          );
        } else {
          if (!this.isValidAppRatesArray(fsConfig.appRates)) {
            reasonsItIsNotNormalized = _.concat(
              reasonsItIsNotNormalized,
              `Funding stream config ${fsType} has invalid appRates array: ${fsConfig.appRates}`
            );
          }
          if (fsConfig.appRates.length != fsConfig.numPeriods) {
            reasonsItIsNotNormalized = _.concat(
              reasonsItIsNotNormalized,
              `Funding stream config ${fsType} appRates array length (${fsConfig.appRates?.length}) does not equal numPeriods (${fsConfig.numPeriods})`
            );
          }
        }
      }
    }

    // other properties such as fedShareLimit or isEligible are optional
    // stash of reasons not normalized for any debugging purposes
    this.reasonsItIsNotNormalized = reasonsItIsNotNormalized;
    return reasonsItIsNotNormalized.length == 0;
  }

  /* return true if val is an array of numbers and every element in it
   * fall between 0 and 1 inclusive.
   */
  isValidAppRatesArray(possibleNonEmptyArray) {
    if (!_.isArray(possibleNonEmptyArray)) return false;
    if (_.isEmpty(possibleNonEmptyArray)) return false;
    for (const elem of possibleNonEmptyArray) {
      if (!this.isValidPercentValue(elem)) return false;
    }
    return true;
  }

  isValidPercentValue(val) {
    return typeof val == 'number' && 0 <= val && val <= 1;
  }

  isEligible() {
    return typeof this.normalizedBudgetCalcConfig.isEligible !== 'boolean'
      ? true
      : this.normalizedBudgetCalcConfig.isEligible;
  }

  getApplicableRules(appData) {
    let applicableRules = [];
    _.each(this?.budgetCalcConfig?.budgetCalcRules, (rule) => {
      let ifPath = rule.ifDataPath;

      if (ifPath) {
        if (contains(rule.equalValues, _.get(appData, ifPath))) {
          applicableRules.push(rule);
        }
      } else {
        applicableRules.push(rule);
      }
    });
    return applicableRules;
  }

  getApplicableCalcRules(applicableRules, appData) {
    let calcRules = [];
    _.each(applicableRules, (ruleToApply) => {
      if (!_.isEmpty(ruleToApply.calculationRules)) {
        _.each(ruleToApply.calculationRules, (calcRule) => {
          const dataValue = _.get(appData, calcRule.ifDataPath);
          const values = _.get(calcRule, 'equalValues');
          const range = _.get(calcRule, 'range');
          const fedShareLimit = _.toNumber(_.get(calcRule, 'fedShareLimit'));
          // Add all calculations and fer share limit
          if (!_.isEmpty(values) && _.indexOf(values, dataValue) > -1) {
            calcRules.push(calcRule);
          } else if (!_.isEmpty(range)) {
            // Array with low and maybe high
            const intValue = _.parseInt(dataValue);
            const low = _.parseInt(_.get(range, 0));
            const high = _.parseInt(_.get(range, 1));
            if (
              !_.isNaN(intValue) &&
              intValue >= low &&
              (_.isNaN(high) || intValue <= high)
            ) {
              calcRules.push(calcRule);
            }
          } else if (!_.isNaN(fedShareLimit)) {
            calcRules.push(calcRule);
          }
        });
      }
    });
    return calcRules;
  }

  /* Reduce array of applicable rules to single object with config properties
   * that could be applied to the original budget calc configuration.
   * Currently only a defaultAppRate property value in a rule is processed.
   * If you have such a property in the array of applicableRules and it is numeric and
   * value is between 1 and 0 then it gets copied to the return object
   */
  reduceRulesArrayToObject(applicableRules) {
    return _.reduce(
      applicableRules,
      (result, rule) => {
        const defaultAppRate = rule?.defaultAppRate;
        if (
          typeof defaultAppRate === 'number' &&
          defaultAppRate >= 0 &&
          defaultAppRate <= 1
        ) {
          result.defaultAppRate = defaultAppRate;
        }
        return result;
      },
      {}
    );
  }

  /* Reduce array of applicable calcRules to a single object with config properties.
   * Such properties might include
   * - defaultAppRate - single numeric value used to build appRates arrays
   * - fedShareLimit - calculator compare computed fedShare to this value to see if it exceeds limit
   * - fedShareLimitMessage - message associated with a fedShare that exceeds the limit.
   * - isEligible - AFG considers SFTA applicants as being ineligible
   * - notEligibleMessage - message associated with being ineligible
   */
  reduceCalcRulesArrayToObject(applicableCalcRules) {
    return _.reduce(
      applicableCalcRules,
      (result, calcRule) => {
        const limit = _.toNumber(_.get(calcRule, 'fedShareLimit'));
        const limitMessage = _.get(calcRule, 'message');
        if (!_.isNaN(limit)) {
          result.fedShareLimit = limit;
          result.fedShareLimitMessage = limitMessage;
        }
        const defaultAppRate = parseFloat(_.get(calcRule, 'defaultAppRate'));
        if (!_.isNaN(defaultAppRate)) {
          result.defaultAppRate = defaultAppRate;
        }
        const isEligible = calcRule?.isEligible;
        const notEligibleMsg = calcRule?.message;
        if (typeof isEligible === 'boolean') {
          result.isEligible = isEligible;
          if (notEligibleMsg) {
            result.notEligibleMessage = notEligibleMsg;
          }
        }
        return result;
      },
      {}
    );
  }
}

export default BudgetCalcRulesEvaluator;
